import React, { useEffect, useState, useCallback, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import axios from "axios";
import { API_BASE_URL, getApiToken } from "../config/authConfig";
import OnboardingExecutionProgress from "./OnboardingExecutionProgress";

function StatusBadge({ status }) {
  const cls =
    status === "Completed"
      ? "status-badge status-completed"
      : status === "Failed"
      ? "status-badge status-failed"
      : status === "In Progress"
      ? "status-badge status-in-progress"
      : status === "Pending License"
      ? "status-badge status-pending-license"
      : "status-badge status-pending";
  return <span className={cls}>{status || "Pending"}</span>;
}

function DetailModal({ request, onClose, onExecute }) {
  const canExecute = request.status === "Pending" || request.status === "Failed";
  const canResume  = request.status === "Pending License";
  const fmt = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric", month: "long", day: "numeric",
    });
  };

  const fmtStart = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr.split("T")[0] + "T12:00:00");
    return isNaN(d) ? "—" : d.toLocaleDateString("en-US", {
      year: "numeric", month: "long", day: "numeric",
    });
  };

  const Row = ({ label, value }) =>
    value ? (
      <div className="detail-row">
        <span className="detail-label">{label}</span>
        <span className="detail-value">{value}</span>
      </div>
    ) : null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <span className="txn-code">{request.transactionNumber}</span>
            <StatusBadge status={request.status} />
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="modal-body">
          <div className="modal-details">
            <Row label="Name"          value={request.employeeName} />
            <Row label="UPN"           value={request.userPrincipalName} />
            <Row label="Employee ID"   value={request.employeeId} />
            <Row label="Contact #"     value={request.contactNumber} />
            <Row label="Employee Type" value={request.employeeType} />
            <Row label="Start Date"    value={fmtStart(request.startDate)} />
            <Row label="Department"    value={request.department} />
            <Row label="Job Title"     value={request.jobTitle} />
            <Row label="Manager"       value={request.manager} />
            <Row label="Location"      value={request.location} />
            <Row label="Office"        value={request.office} />
            <Row label="Address"       value={request.address} />
            <Row label="District"      value={request.district} />
            <Row label="Region"        value={request.region} />
            <Row label="Applications"  value={request.applications} />
            <Row label="Submitted By"  value={request.submittedBy} />
            <Row label="Date Submitted" value={fmt(request.dateSubmitted)} />
          </div>

          {request.transactionLog && (
            <div className="action-log">
              <div className="action-log-label">Transaction Log</div>
              <pre className="action-log-content">{request.transactionLog}</pre>
            </div>
          )}

        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Close</button>
          {(canExecute || canResume) && (
            <button className="btn-primary" onClick={() => onExecute(request)}>
              {canResume ? "Resume After License Approval" : "Execute Automation"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function OnboardingHistoryView({ onNewRequest }) {
  const { accounts, instance } = useMsal();
  const user = accounts[0] || {};

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);

  // Execution progress state
  const [showProgress, setShowProgress] = useState(false);
  const [progressRunning, setProgressRunning] = useState(false);
  const [progressLog, setProgressLog] = useState("");
  const [progressTransaction, setProgressTransaction] = useState("");
  const [progressEmployee, setProgressEmployee] = useState("");
  const [progressPendingLicense, setProgressPendingLicense] = useState(false);
  const pollIntervalRef = useRef(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getApiToken(instance);
      const res = await axios.get(`${API_BASE_URL}/onboarding/requests`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRequests(res.data.requests || []);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load onboarding requests.");
    } finally {
      setLoading(false);
    }
  }, [instance]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const filtered = requests.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      r.transactionNumber?.toLowerCase().includes(q) ||
      r.employeeName?.toLowerCase().includes(q) ||
      r.userPrincipalName?.toLowerCase().includes(q) ||
      r.department?.toLowerCase().includes(q) ||
      r.jobTitle?.toLowerCase().includes(q)
    );
  });

  const formatDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric",
    });
  };

  const formatStart = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr.split("T")[0] + "T12:00:00");
    return isNaN(d) ? "—" : d.toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric",
    });
  };

  const handleExecuteFromHistory = useCallback(async (request) => {
    setSelectedRequest(null);
    setProgressTransaction(request.transactionNumber);
    setProgressEmployee(request.employeeName);
    setProgressLog(request.transactionLog || "");
    setProgressRunning(true);
    setProgressPendingLicense(false);
    setShowProgress(true);

    pollIntervalRef.current = setInterval(async () => {
      try {
        const t = await getApiToken(instance);
        const statusRes = await axios.get(`${API_BASE_URL}/onboarding/status`, {
          params: { itemId: request._itemId },
          headers: { Authorization: `Bearer ${t}` },
        });
        setProgressLog(statusRes.data.transactionLog || "");
      } catch { /* ignore */ }
    }, 5000);

    try {
      const token = await getApiToken(instance);
      const execRes = await axios.post(
        `${API_BASE_URL}/onboarding/execute`,
        { itemId: request._itemId, transactionNumber: request.transactionNumber, submittedBy: request.submittedBy },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, timeout: 600000 }
      );
      clearInterval(pollIntervalRef.current);
      const isPending = execRes.data.status === "Pending License";
      setProgressLog(execRes.data.transactionLog || "");
      setProgressRunning(false);
      setProgressPendingLicense(isPending);
      await new Promise((r) => setTimeout(r, isPending ? 4000 : 2000));
    } catch {
      clearInterval(pollIntervalRef.current);
      setProgressRunning(false);
    }

    setShowProgress(false);
    fetchRequests();
  }, [instance, fetchRequests]);

  return (
    <>
      {showProgress && (
        <OnboardingExecutionProgress
          transactionNumber={progressTransaction}
          employeeName={progressEmployee}
          transactionLog={progressLog}
          isRunning={progressRunning}
          isPendingLicense={progressPendingLicense}
        />
      )}
    <div className="page-wrapper">
      <header className="app-header">
        <div className="header-inner">
          <div className="header-brand">
            <span className="header-title">Employee Onboarding</span>
          </div>
          <div className="header-actions">
            <button className="btn-primary btn-header" onClick={onNewRequest}>
              + New Request
            </button>
            <span className="header-username">{user.name || user.username}</span>
          </div>
        </div>
      </header>

      <main className="history-container">
        <div className="history-title-row">
          <div>
            <h2 className="history-title">Onboarding Requests</h2>
            <p className="history-subtitle">
              {requests.length} total record{requests.length !== 1 ? "s" : ""}
            </p>
          </div>
          <div className="history-toolbar">
            <input
              type="text"
              className="history-search"
              placeholder="Search by name, UPN, department..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button className="btn-secondary" onClick={fetchRequests}>
              Refresh
            </button>
            <button className="btn-primary" onClick={onNewRequest}>
              + New Request
            </button>
          </div>
        </div>

        {error && <div className="history-error">{error}</div>}

        {loading && (
          <div className="history-loading">
            <span className="search-spinner" style={{ width: 24, height: 24, borderWidth: 3 }} />
            <p>Loading onboarding requests...</p>
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="history-empty">
            <p>{search ? "No records match your search." : "No onboarding requests found."}</p>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="history-card">
            <table className="history-table">
              <thead>
                <tr>
                  <th>Transaction Number</th>
                  <th>Employee Name</th>
                  <th>User Principal Name</th>
                  <th>Department</th>
                  <th>Status</th>
                  <th>Start Date</th>
                  <th>Date Submitted</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr
                    key={i}
                    className="history-row-clickable"
                    onClick={() => setSelectedRequest(r)}
                  >
                    <td><span className="txn-code">{r.transactionNumber || "—"}</span></td>
                    <td className="td-name">{r.employeeName || "—"}</td>
                    <td className="td-upn">{r.userPrincipalName || "—"}</td>
                    <td>{r.department || "—"}</td>
                    <td><StatusBadge status={r.status} /></td>
                    <td className="td-date">{formatStart(r.startDate)}</td>
                    <td className="td-date">{formatDate(r.dateSubmitted)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {selectedRequest && (
        <DetailModal
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onExecute={handleExecuteFromHistory}
        />
      )}
    </div>
    </>
  );
}
